import React, { forwardRef, useRef, useState, useImperativeHandle } from "react";
import { RCCGrid, RCCHookToast, RCCRefModalProps, RCCSelectOptionsCard, RCCServicesApi, RCCTextArea } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import Photo from "src/components/Photo";

import Title from "src/components/Title";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { css } from "styled-components";
import { errorsCancelConfirmSchema, errorsCancelSchema } from "../schemas";

interface ICancel{
	inb005Id: number;
	inb005Efa005Nome: string;
	inb005Efa005Imagem: string;
	inb005Motivo: string;
}

export interface IActionCancelRef{
    openModalActionCancel: (id: number, titleAction: string, onCallback?: Function) => void;
	openModalActionCancelConfirm: (id: number, titleAction: string, onCallback?: Function) => void;
}

const ModalCancel: React.ForwardRefRenderFunction<IActionCancelRef> = (_, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
	
	// Modal cancel
	const modalCancel = useRef<RCCRefModalProps>(null);
	const onCallbackCancel = useRef<Function | undefined>(undefined);
	const [actionId, setActionId] = useState(0);
	const [subtitle, setSubtitle] = useState<string | undefined>(undefined);
	const { handleSubmit: handleSubmitCancel, register: registerCancel, control: controlCancel, formState: { errors: errorsCancel }, clearErrors: clearErrorsCancel, setError: setErrorCancel, reset: resetCancel } = useForm();
	const openModalActionCancel = async (id: number, titleAction: string, onCallback?: Function) => {
		resetCancel();
		onCallbackCancel.current = onCallback;
		setActionId(id);
		setSubtitle(titleAction);
		modalCancel.current?.openModal();	
	};
	const onSubmit = async (data: any) => {
		try{
			clearErrorsCancel();
			modalCancel.current?.setLoadingButton();

			await errorsCancelSchema(translate).validate({...data }, {
				abortEarly: false
			});

			await RCCServicesApi.postRequest({
				method: "Actions/InsertCancel",
				body: {
					actionId,
					actionName: subtitle,
					...data
				}
			});
			
			modalCancel.current?.resetLoadingButton();
			modalCancel.current?.closeModal();

			if(onCallbackCancel.current)
				onCallbackCancel.current();

		}catch(err){
			modalCancel.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorCancel)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}
			standardErrorMessages(err, translate("Erro ao inserir cancelamento"), addToast);
		}
	};

	// Modal confirm cancel
	const modalCancelConfirm = useRef<RCCRefModalProps>(null);
	const [cancel, setCancel] = useState<ICancel>({} as ICancel);
	const [showReason, setShowReason] = useState(false);
	const openModalActionCancelConfirm = async (id: number, titleAction: string, onCallback?: Function) => {
		resetCancel();
		try{
			setLoading(true);

			const {data} = await RCCServicesApi.getRequest({
				method: "Actions/SelectCancel",
				urlParameters: {
					id
				}
			});
			setCancel(data);
			onCallbackCancel.current = onCallback;
			setLoading(false);
			setActionId(id);
			setShowReason(false);
			setSubtitle(titleAction);
			modalCancelConfirm.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar cancelamento"), addToast);
		}
	};
	const onSubmitConfirm = async (data: any) => {
		try{
			clearErrorsCancel();
			modalCancelConfirm.current?.setLoadingButton();

			await errorsCancelConfirmSchema(translate).validate({...data }, {
				abortEarly: false
			});

			await RCCServicesApi.putRequest({
				method: "Actions/EditCancelConfirm",
				body: {
					actionId,
					actionName: subtitle,
					id: cancel.inb005Id,
					...data
				}
			});
			
			modalCancelConfirm.current?.resetLoadingButton();
			modalCancelConfirm.current?.closeModal();

			if(onCallbackCancel.current)
				onCallbackCancel.current();

		}catch(err){
			modalCancelConfirm.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorCancel)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}
			standardErrorMessages(err, translate("Erro ao cancelar ação"), addToast);
		}
	};
	const onChangeApprove = (value: string) => {
		setShowReason(value === "N");
	};

	useImperativeHandle(ref, () => ({
		openModalActionCancel,
		openModalActionCancelConfirm
	}));

	return (
		<>
			{/* Modal create cancel */}
			<Modal 
				ref={modalCancel}
				header={{
					title: translate("Cancelar"),
					subtitle: `${translate("Ação")}: ${subtitle}`,
					icon: {
						name: "close"
					}
				}}
				onSubmit={handleSubmitCancel(onSubmit)}
				styleInline={css`
					position: absolute;
					top: 20px;
					height: 300px;
					min-width: 300px;
					width: 50vw;
				`}
			>
				<RCCGrid container item>
					<RCCTextArea 
						name="description"
						label={translate("Descrição")}
						maxLength={4000}
						required
						error={errorsCancel.description}
						register={registerCancel}
					/>
				</RCCGrid>
			</Modal>

			{/* Modal approve/reject cancel */}
			<Modal 
				ref={modalCancelConfirm}
				header={{
					title: `${translate("Aprovar")}/${translate("rejeitar")} ${translate("cancelamento")}`,
					subtitle: `${translate("Ação")}: ${subtitle}`,
					icon: {
						name: "close"
					}
				}}
				onSubmit={handleSubmitCancel(onSubmitConfirm)}
				styleInline={css`
					position: absolute;
					top: 20px;
					min-height: 450px;
					min-width: 300px;
					height: 40vh;
					width: 50vw;
				`}
			>
				<RCCGrid 
					container 
					item 
					alignItems="center"
				>
					<Title color="grey" size="sm">{translate("Usuário")}: </Title>
					<Photo 
						name={cancel.inb005Efa005Nome}
						link={cancel.inb005Efa005Imagem && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${cancel.inb005Efa005Imagem}`}
						hover={false}
					/>
					<Title color="grey" size="xs" bold>{cancel.inb005Efa005Nome}</Title>
				</RCCGrid>

				<RCCGrid 
					container
					item
					direction="column"
				>
					<Title color="grey" size="sm">{translate("Motivo")}: </Title>
					<Title color="grey" size="xs" bold>{cancel.inb005Motivo}</Title>
				</RCCGrid>
				<RCCGrid container item>
					<RCCSelectOptionsCard
						control={controlCancel}
						name="approve"
						label=" "
						defaultValue="S"
						options={[{
							label: translate("Aprovar"),
							value: "S"
						},{
							label: translate("Reprovar"),
							value: "N"
						}]}
						onChange={(value: string) => onChangeApprove(value)}
					/>		
				</RCCGrid>
				{showReason && (
					<RCCGrid container item>
						<RCCTextArea 
							name="reason"
							label={translate("Motivo")}
							maxLength={4000}
							required
							error={errorsCancel.reason}
							register={registerCancel}
						/>
					</RCCGrid>
				)}
			</Modal>
		</>
	);
};

export default forwardRef(ModalCancel);
